import { useState, useEffect, useCallback } from 'react';

interface FullscreenManager {
  isFullscreen: boolean;
  requestFullscreen: () => Promise<boolean>;
  exitFullscreen: () => Promise<void>;
  isSupported: boolean;
}

/**
 * Custom hook for managing fullscreen mode with cross-browser compatibility
 * Supports Chrome, Firefox, Safari, and Edge with vendor-prefixed APIs
 */
export const useFullscreen = (): FullscreenManager => {
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  // Check if Fullscreen API is supported
  const isSupported = !!(
    document.fullscreenEnabled ||
    (document as any).webkitFullscreenEnabled ||
    (document as any).mozFullScreenEnabled ||
    (document as any).msFullscreenEnabled
  );

  /**
   * Request fullscreen mode with cross-browser support
   * @returns Promise<boolean> - true if successful, false otherwise
   */
  const requestFullscreen = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      console.warn('Fullscreen API is not supported in this browser');
      return false;
    }

    try {
      const element = document.documentElement;

      // Try standard API first
      if (element.requestFullscreen) {
        await element.requestFullscreen();
        return true;
      }
      // Webkit (Safari, older Chrome)
      else if ((element as any).webkitRequestFullscreen) {
        await (element as any).webkitRequestFullscreen();
        return true;
      }
      // Firefox
      else if ((element as any).mozRequestFullScreen) {
        await (element as any).mozRequestFullScreen();
        return true;
      }
      // IE/Edge
      else if ((element as any).msRequestFullscreen) {
        await (element as any).msRequestFullscreen();
        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to enter fullscreen:', error);
      return false;
    }
  }, [isSupported]);

  /**
   * Exit fullscreen mode with cross-browser support
   * @returns Promise<void>
   */
  const exitFullscreen = useCallback(async (): Promise<void> => {
    try {
      // Try standard API first
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      }
      // Webkit (Safari, older Chrome)
      else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      }
      // Firefox
      else if ((document as any).mozCancelFullScreen) {
        await (document as any).mozCancelFullScreen();
      }
      // IE/Edge
      else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }
    } catch (error) {
      console.error('Failed to exit fullscreen:', error);
      throw error;
    }
  }, []);

  /**
   * Handle fullscreen change events
   */
  const handleFullscreenChange = useCallback(() => {
    const isCurrentlyFullscreen = !!(
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement
    );

    setIsFullscreen(isCurrentlyFullscreen);
  }, []);

  // Set up event listeners for fullscreen changes
  useEffect(() => {
    // Add listeners for all browser variants
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // Check initial state
    handleFullscreenChange();

    // Cleanup listeners on unmount
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [handleFullscreenChange]);

  return {
    isFullscreen,
    requestFullscreen,
    exitFullscreen,
    isSupported,
  };
};
